<script setup lang="ts">
import { Trash2 } from 'lucide-vue-next'

import type { ImageFile } from '@/types/types'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    initialImages: ImageFile[]
    images: ImageFile[]
    deletedImages: number[]
    singleImage?: boolean
    disabled?: boolean
    aspectRatio?: number | [number, number]
    crop?: boolean
    fitThumbnail?: boolean
  }>(),
  {
    initialImages: () => [],
    images: () => [],
    deletedImages: () => [],
    singleImage: false,
    disabled: false,
    aspectRatio: undefined,
    crop: false,
    fitThumbnail: false
  }
)

const cropSrc = ref<string | null>(null)
const pendingFile = ref<File | null>(null)

const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const errorMessage = ref('')
const selectedImageIndex = ref<number[]>([])

const triggerFileInput = () => {
  if (props.disabled) return
  fileInput.value.click()
}

const selectImage = (imageId: number) => {
  if (props.disabled) return

  if (props.singleImage) {
    // En mode singleImage, on supprime directement l'image
    deleteSingleImage(imageId)
  } else {
    // Mode normal avec sélection multiple
    if (selectedImageIndex.value.includes(imageId)) {
      selectedImageIndex.value = selectedImageIndex.value.filter((i) => i !== imageId)
    } else {
      selectedImageIndex.value.push(imageId)
    }
  }
}

const emit = defineEmits(['update:images', 'selected-image', 'update:deleted-images'])

const onFileSelected = (event) => {
  if (props.disabled) return

  const files = event.target.files
  if (files.length > 0) {
    // En mode singleImage, on ne prend que le premier fichier
    const filesToProcess = props.singleImage ? [files[0]] : Array.from(files)
    processFiles(filesToProcess)
  }
  fileInput.value.value = ''
}

const onDrop = (event) => {
  if (props.disabled) return

  isDragging.value = false
  const files = event.dataTransfer.files
  if (files.length > 0) {
    // En mode singleImage, on ne prend que le premier fichier
    const filesToProcess = props.singleImage ? [files[0]] : Array.from(files)
    processFiles(filesToProcess)
  }
}

const compressImage = (img: HTMLImageElement, file: File, maxWidth = 1200, quality = 0.8): Promise<string> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    let width = img.width
    let height = img.height

    if (width > maxWidth) {
      height = Math.round((height * maxWidth) / width)
      width = maxWidth
    }

    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    ctx?.drawImage(img, 0, 0, width, height)

    const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
    const compressedBase64 = canvas.toDataURL(mimeType, quality)
    resolve(compressedBase64)
  })
}

const validateFile = (file: File): boolean => {
  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    errorMessage.value = 'Format de fichier non pris en charge. Utilisez PNG ou JPG.'
    return false
  }
  const fileSizeKB = file.size / 1024
  if (fileSizeKB < 5 || fileSizeKB > 8192) {
    errorMessage.value = 'La taille du fichier doit être entre 5ko et 8Mo.'
    return false
  }
  return true
}

const ingestFile = (file: File) => {
  const fileSizeKB = file.size / 1024
  const reader = new FileReader()
  reader.onload = async (e) => {
    if (!e.target?.result) return
    const base64Url = e.target.result as string

    const img = new Image()
    img.onload = async () => {
      const shouldCompress = fileSizeKB > 500 || img.width > 1200 || img.height > 1200
      const finalBase64 = shouldCompress ? await compressImage(img, file) : base64Url

      const newImage = {
        file,
        id: new Date().getTime() + Math.random(),
        path: finalBase64,
        name: file.name,
        size: file.size,
        width: img.width,
        height: img.height,
        blob: file,
        type: 'uploaded'
      }

      if (props.singleImage) {
        emit('update:images', [newImage])
      } else {
        emit('update:images', [...props.images, newImage])
      }
    }

    img.onerror = () => {
      errorMessage.value = "Impossible de charger l'image. Le fichier semble corrompu."
    }

    img.src = base64Url
  }
  reader.readAsDataURL(file)
}

const cropQueue = ref<File[]>([])

const openCropDialog = (file: File) => {
  const reader = new FileReader()
  reader.onload = (e) => {
    if (e.target?.result) {
      cropSrc.value = e.target.result as string
      pendingFile.value = file
    }
  }
  reader.readAsDataURL(file)
}

const processNextInQueue = () => {
  const next = cropQueue.value.shift()
  if (next) openCropDialog(next)
}

const onCropConfirm = (blob: Blob) => {
  const original = pendingFile.value
  cropSrc.value = null
  pendingFile.value = null
  if (!original) return
  const croppedFile = new File([blob], original.name, { type: blob.type })
  ingestFile(croppedFile)
  nextTick(() => processNextInQueue())
}

const onCropCancel = () => {
  cropSrc.value = null
  pendingFile.value = null
  cropQueue.value = []
}

const processFiles = async (files: FileList | File[]) => {
  errorMessage.value = ''

  const validFiles: File[] = []
  for (const file of Array.from(files)) {
    if (!validateFile(file)) return
    validFiles.push(file)
  }

  if (props.aspectRatio || props.crop) {
    cropQueue.value = validFiles
    processNextInQueue()
    return
  }

  for (const file of validFiles) {
    try {
      ingestFile(file)
    } catch (error) {
      console.error("Erreur lors de la conversion de l'image en base64:", error)
      errorMessage.value = "Erreur lors de la conversion de l'image en base64."
    }
  }
}

const displayImages = computed(() => {
  return [...props.initialImages, ...props.images].filter((image) => !props.deletedImages.includes(image.id))
})

const deleteSingleImage = (imageId: number) => {
  const isInitialImage = props.initialImages.some((img) => img.id === imageId)

  if (isInitialImage) {
    const updatedDeletedImages = [...props.deletedImages, imageId]
    emit('update:deleted-images', updatedDeletedImages)
  } else {
    const updatedImages = props.images.filter((img) => img.id !== imageId)
    emit('update:images', updatedImages)
  }
}

const deleteImage = () => {
  const imagesToDelete = selectedImageIndex.value.filter((imageId) =>
    props.initialImages.some((img) => img.id === imageId)
  )
  const localImagesToRemove = selectedImageIndex.value.filter((imageId) =>
    props.images.some((img) => img.id === imageId)
  )

  if (imagesToDelete.length > 0) {
    const updatedDeletedImages = [...props.deletedImages, ...imagesToDelete]
    emit('update:deleted-images', updatedDeletedImages)
  }

  if (localImagesToRemove.length > 0) {
    const updatedImages = props.images.filter((img) => !localImagesToRemove.includes(img.id))
    emit('update:images', updatedImages)
  }

  selectedImageIndex.value = []
}

const resetImages = () => {
  emit('update:images', [])
}

defineExpose({
  resetImages
})
</script>

<template>
  <div class="w-full">
    <div
      v-show="displayImages.length === 0"
      class="border border-dashed border-input rounded-lg p-6 flex flex-col items-center justify-center"
      :class="[disabled ? 'bg-muted cursor-not-allowed' : 'bg-muted cursor-pointer', { 'opacity-50': disabled }]"
    >
      <div
        class="w-full h-32 flex flex-col items-center justify-center"
        :class="{ 'bg-blue-50': isDragging && !disabled }"
        @click="triggerFileInput"
        @dragover.prevent="!disabled && (isDragging = true)"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="!disabled && onDrop($event)"
      >
        <Button variant="outline" class="mb-4" :disabled="disabled">
          {{ t('uploader.selectOrImportFile') }}
        </Button>
        <p class="text-muted-foreground text-sm">{{ t('uploader.fileSize') }}</p>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          accept="image/png, image/jpeg"
          :multiple="!singleImage"
          :disabled="disabled"
          @change="onFileSelected"
        />
      </div>
    </div>

    <div v-if="displayImages.length > 0" class="w-full">
      <div class="flex items-start flex-wrap gap-4">
        <!-- Bouton d'ajout d'image (caché en mode singleImage si une image existe déjà) -->
        <div
          v-if="!singleImage || displayImages.length === 0"
          class="w-[120px] aspect-square border border-dashed border-input rounded-lg flex items-center justify-center cursor-pointer bg-muted flex-col text-center"
          :class="[disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-accent']"
          @click="triggerFileInput"
        >
          <BaseIcon name="HeroPlusIconSolid" class="h-8 w-8 text-muted-foreground" />
          <p class="text-muted-foreground text-xs mt-2">{{ t('uploader.fileSize') }}</p>
        </div>

        <div
          v-for="image in displayImages"
          :key="image.id"
          class="relative rounded-xl border border-input group p-[3px]"
          :class="fitThumbnail ? 'h-[120px]' : 'w-[120px] aspect-square'"
        >
          <!-- Photoshop-style transparency checkerboard -->
          <div
            class="absolute inset-[3px] rounded-lg z-0 opacity-30"
            style="
              background-image:
                linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%);
              background-size: 20px 20px;
              background-position:
                0 0,
                0 10px,
                10px -10px,
                -10px 0px;
            "
          ></div>

          <img
            :src="image.path"
            class="border border-input rounded-lg relative z-10"
            :class="fitThumbnail ? 'h-full w-auto object-contain' : 'w-full h-full object-cover'"
            alt="Image uploadée"
          />

          <div
            v-if="singleImage && !disabled"
            class="absolute z-20 -top-2 -right-2 w-6 h-6 border border-input bg-white text-gray-800 hover:text-red-500 hover:border-red-500 hover:bg-red-50 rounded-full cursor-pointer flex items-center justify-center transition-colors duration-200"
            @click="selectImage(image.id)"
          >
            <BaseIcon name="HeroTrashIconOutline" class="h-4 w-4" />
          </div>

          <div
            v-if="!singleImage"
            :class="[
              'absolute z-20 top-2 right-2 border border-input w-4 h-4 bg-white rounded-md cursor-pointer flex items-center justify-center',
              { 'opacity-50 cursor-not-allowed': disabled }
            ]"
            @click="selectImage(image.id)"
          >
            <BaseIcon
              v-if="selectedImageIndex.includes(image.id)"
              name="HeroCheckIconSolid"
              class="h-3 w-3 text-primary"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="selectedImageIndex.length > 0" class="mb-3 flex justify-end">
      <Button variant="outline-destructive" size="sm" @click.stop="deleteImage">
        <Trash2 />
        {{ t('uploader.deleteSelection') }}
      </Button>
    </div>

    <div v-if="errorMessage" class="mt-4 text-red-500 text-sm">
      {{ errorMessage }}
    </div>

    <ImageCropDialog :src="cropSrc" :aspect-ratio="aspectRatio" @confirm="onCropConfirm" @cancel="onCropCancel" />
  </div>
</template>
